// Message Box, fourth pane — how much each device talked on one day.
//
// One line per device: the unique number, how many calls, and where it is.
// Busiest first, because the question this answers is "what is chattering".
//
// ⚠️ ONE DAY AT A TIME, and that is a data constraint rather than a choice: the
// server's message list is capped at 500 rows, which covers a single day
// (measured: 465 rows against a total of 466) and truncates 97% of a week. See
// core/devices/messageStats.
//
// ⚠️ THE CLIENT COLUMN WILL READ ZERO ON ALMOST EVERY DEVICE, and that is the
// truth rather than a bug. Client-side logging is a per-device switch
// (`SaveOutGoingMessages`) and it is on for ONE device out of 203; the device
// side (`AllowIncoming`) is on for 39. So a device absent from this list is
// usually not a silent device, it is an unlogged one — which the footer says,
// because "no traffic" and "not recorded" look identical in a table and mean
// opposite things.

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, TextField, IconButton, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, Chip, Tooltip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchDayCallCounts, dayKey, summarise } from '../../../core/devices/messageStats';

export default function CallCountsPane({ onPickDevice }) {
  const [date, setDate] = useState(() => dayKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (d) => {
    setLoading(true);
    try {
      setData(await fetchDayCallCounts(d));
    } catch (e) {
      setData({ date: d, rows: [], listRows: 0, truncated: false });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(date); }, [date, load]);

  const rows = data?.rows || [];
  const sum = summarise(rows);
  const cell = { py: 0.4, fontSize: 11.5 };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <Typography variant="overline" color="text.secondary" sx={{ flex: 1, lineHeight: 1.6 }}>
          Calls per device
        </Typography>
        <TextField
          type="date"
          size="small"
          value={date}
          onChange={(e) => e.target.value && setDate(e.target.value)}
          inputProps={{ style: { fontSize: 12, padding: '4px 6px' } }}
          sx={{ width: 140 }}
        />
        <IconButton size="small" onClick={() => load(date)} disabled={loading}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.5 }}>
        <Chip size="small" variant="outlined" label={`${sum.devices} devices`} />
        <Chip size="small" variant="outlined" label={`${sum.total} calls`} />
        <Chip size="small" variant="outlined" color="info" label={`${sum.device} by device`} />
        <Tooltip title="Client calls are only recorded for devices with SaveOutGoingMessages switched on — currently almost none">
          <Chip
            size="small"
            variant="outlined"
            color={sum.client ? 'warning' : 'default'}
            label={`${sum.client} by client`}
          />
        </Tooltip>
      </Box>

      {loading && !data ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}><CircularProgress size={22} /></Box>
      ) : (
        <TableContainer sx={{ flex: 1, minHeight: 0 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...cell, fontWeight: 700 }}>Device</TableCell>
                <TableCell sx={{ ...cell, fontWeight: 700 }} align="right">Client</TableCell>
                <TableCell sx={{ ...cell, fontWeight: 700 }} align="right">Device</TableCell>
                <TableCell sx={{ ...cell, fontWeight: 700 }} align="right">Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} sx={{ ...cell, color: 'text.secondary' }}>
                    Nothing recorded for this day.
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => (
                <TableRow
                  key={r.uniqueNumber}
                  hover
                  // Clicking a row aims the console at that device — this pane
                  // is next to a publish box, and the number you have just found
                  // chattering is the one you want to talk to.
                  onClick={() => onPickDevice && onPickDevice(r.uniqueNumber)}
                  sx={{ cursor: onPickDevice ? 'pointer' : 'default' }}
                >
                  <TableCell sx={cell}>
                    <Box sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{r.uniqueNumber}</Box>
                    {/* Location · Room · Device on the same line, as asked —
                        it is what turns a number into a place. */}
                    <Box sx={{ color: 'text.secondary', fontSize: 10.5 }}>
                      {[r.locationName, r.roomName, r.deviceName].filter(Boolean).join(' · ')}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ ...cell, opacity: r.client ? 1 : 0.4 }} align="right">
                    {r.client}
                  </TableCell>
                  <TableCell sx={cell} align="right">{r.device}</TableCell>
                  <TableCell sx={{ ...cell, fontWeight: 700 }} align="right">{r.total}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ⚠️ Both of these are the difference between a number that means
          something and one that does not. */}
      {data?.truncated && (
        <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
          The server returned its maximum of 500 messages, so the Client/Device
          split is a sample — Total is still exact.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
        Only devices with message logging switched on appear here. A device that
        is missing is usually unlogged rather than silent.
      </Typography>
    </Box>
  );
}
